/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type { Database } from "../../../__definitions__/database-definitions";
import {
  type GetAllOptions,
  SupabaseService,
} from "../../../server/supabase/base-supabase-service";
import type { UUID } from "../../../utils/uuid";

export type AssetRow = NonNullable<Database["public"]["Tables"]["digital_asset"]["Row"]>;
export type Asset = Omit<AssetRow, "tags"> & { tags: string[] };
export type AssetUpsert = (Database["public"]["Tables"]["digital_asset"] & {
  [k: string]: never;
})["Insert"];

type AssetTagRelation = { tag: Pick<Database["public"]["Tables"]["tag"]["Row"], "name"> | null };
type AssetWithTagRelationsRow = AssetRow & { digital_asset_tag?: AssetTagRelation[] | null };

export const toAsset = (asset: AssetRow | AssetWithTagRelationsRow): Asset => {
  const { digital_asset_tag, ...base } = asset as AssetWithTagRelationsRow;
  const relationTags =
    digital_asset_tag?.flatMap((relation) => (relation?.tag?.name ? [relation.tag.name] : [])) ??
    null;

  return {
    ...base,
    tags: relationTags ?? base.tags ?? [],
  };
};

export const toMaybeAsset = (asset?: AssetRow | null): Asset | null => {
  if (!asset) return null;
  return toAsset(asset);
};

export const toAssets = (assets?: AssetRow[] | null): Asset[] => {
  if (!assets) return [];
  return assets.map(toAsset);
};

export class SupabaseDigitalAssetService extends SupabaseService<"digital_asset"> {
  table = "digital_asset" as const;

  readonly getAllWithTagsQuery = this._getAllWithTagsQuery;
  _getAllWithTagsQuery() {
    return this.client
      .from(this.table)
      .select<
        "*, digital_asset_tag(tag(name))",
        AssetWithTagRelationsRow
      >("*, digital_asset_tag(tag(name))");
  }

  async getAllWithTags(account_id: UUID | null, options: GetAllOptions<AssetWithTagRelationsRow>) {
    return await this.getAllFromSelection(account_id, this.getAllWithTagsQuery(), options);
  }
}
